import "./VersionPop.css";
import { useEffect, useState, useContext, useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Badge from "@mui/material/Badge";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

import { AuthContext } from "@contexts/AuthContext";
import { fetchVersionOptionsData } from "@libs/apiRequest.js";
import { getCSSThemeByRole } from "@libs/themeHelper";
import Spinner from "@common-components/Spinner/Spinner";
import ErrorMessage from "@common-components/ErrorMessage/ErrorMessage";

const VersionPop = ({ onModelSelect, model, selectedTab }) => {
  const [open, setOpen] = useState(false);
  const [versionDataObj, setVersionDataObj] = useState({});
  const [error, setError] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const { currentUserRole } = useContext(AuthContext);

  let selectedModelID = model.id || "";
  const isBadgeDisabled = versionDataObj?.versionList?.length < 2;
  const isDisabled = isLoading || isBadgeDisabled;

  useEffect(() => {
    // fetch all the versions of a model from control-versions excel sheet
    const fetchVersionDataObj = async () => {
      try {
        const versionDataObjData = await fetchVersionOptionsData(
          selectedTab,
          selectedModelID
        );
        setVersionDataObj(versionDataObjData);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchVersionDataObj();
  }, [selectedModelID, selectedTab]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleListItemClick = (selectedVersion) => {
    onModelSelect({
      ...model,
      selectedVersion: selectedVersion,
    });
  };

  const CSStheme = useMemo(
    () => getCSSThemeByRole(currentUserRole),
    [currentUserRole]
  );

  return (
    <>
      <Badge
        badgeContent={versionDataObj?.versionList?.length}
        color="secondary"
        invisible={isDisabled}
        sx={{ marginLeft: "auto", marginRight: "10px" }}
      >
        <MoreHorizIcon
          sx={{
            pr: "6px",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            color: isDisabled ? "grey" : "secondary.main",
            cursor: isDisabled ? "not-allowed" : "pointer",

            "&:hover": {
              color: isDisabled ? "grey" : "primary.btn_hover",
              cursor: isDisabled ? "not-allowed" : "pointer",
            },
          }}
          onClick={isDisabled ? undefined : handleClickOpen}
        />
      </Badge>

      <Dialog
        open={open}
        onClose={handleClose}
        sx={{ overflowY: "auto", scrollbarColor: "grey transparent" }}
        slotProps={{
          paper: {
            sx: {
              minWidth: "500px",
              maxHeight: "80vh", // allow dialog to scroll if it gets long
              padding: "20px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "center",
            },
          },
        }}
      >
        <DialogTitle
          variant="h5"
          sx={{
            color: "secondary.main",
            textAlign: "center",
            fontWeight: "bold",
            marginBottom: "16px", // use fixed spacing instead of auto
          }}
        >
          Version List
        </DialogTitle>

        {isLoading ? (
          <Spinner />
        ) : error ? (
          <ErrorMessage>{error}</ErrorMessage>
        ) : (
          <div
            data-theme={CSStheme}
            style={{
              width: "100%",
            }}
          >
            <List
              sx={{
                width: "100%",
                maxHeight: "60vh", // set max height to allow scrolling
                overflowY: "auto",
                scrollbarColor: "grey transparent",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "10px",
              }}
            >
              {Array.isArray(versionDataObj?.versionList) &&
                versionDataObj.versionList.map((versionOption) => (
                  <ListItem
                    key={versionOption}
                    disableGutters
                    sx={{
                      width: "90%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "0",
                      marginBottom: "8px",
                    }}
                  >
                    <ListItemButton
                      onClick={() => handleListItemClick(versionOption)}
                      sx={{
                        maxWidth: "60%",
                        borderRadius: "8px",
                        backgroundColor: "primary.main",
                        color: "white",
                        justifyContent: "flex-start",
                        paddingLeft: "16px",
                        transition: "all 0.2s ease-in-out",
                        boxShadow:
                          "0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)",
                        "&:hover": {
                          backgroundColor: "white",
                          color: "primary.main",
                        },
                      }}
                    >
                      <ListItemText
                        primary={`${versionOption}${
                          model.defaultVersion === versionOption
                            ? " (default)"
                            : ""
                        }`}
                        slotProps={{
                          primary: {
                            sx: {
                              fontWeight: 600,
                              fontSize: "1rem",
                            },
                          },
                        }}
                      />
                    </ListItemButton>

                    <div
                      style={{
                        width: "40%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                      }}
                    >
                      <div
                        style={{
                          maxWidth: "100%",
                          width: "fit-content",
                          backgroundColor: "var(--secondary-color)",
                          color: "white",
                          padding: "4px 10px",
                          borderRadius: "16px",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                          whiteSpace: "normal", // allow wrapping
                          wordBreak: "break-word", // break long words
                          marginLeft: "12px",
                          display: "flex",
                          alignItems: "center",
                          cursor: "default",
                        }}
                      >
                        {versionDataObj.versionTagsMap[versionOption] ||
                          "No tag"}
                      </div>
                    </div>
                  </ListItem>
                ))}
            </List>
          </div>
        )}
      </Dialog>
    </>
  );
};

export default VersionPop;
